/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * The slice of an HTTP response a mapped service error keeps: the status and
 * the response headers, keys lower-cased.
 */
export interface THttpContext {
  status: number;
  headers: Record<string, string>;
}

/** Reads `property` off an unknown value without assuming its shape. */
function readProperty(value: unknown, property: string): unknown {
  if (typeof value !== "object" || value === null) {
    return undefined;
  }
  return (value as Record<string, unknown>)[property];
}

/**
 * Copies axios headers into a plain object with lower-cased keys, keeping only
 * string and number values. AxiosHeaders already lower-cases its own keys, but
 * a hand-built mock or a non-axios transport may not.
 */
function normaliseHeaders(rawHeaders: unknown): Record<string, string> {
  const headers: Record<string, string> = {};
  if (typeof rawHeaders !== "object" || rawHeaders === null) {
    return headers;
  }
  const record = rawHeaders as Record<string, unknown>;
  const keys: string[] = Object.keys(record);
  for (let index = 0; index < keys.length; index++) {
    const value: unknown = record[keys[index]];
    if (typeof value === "string") {
      headers[keys[index].toLowerCase()] = value;
    } else if (typeof value === "number") {
      headers[keys[index].toLowerCase()] = String(value);
    }
  }
  return headers;
}

/**
 * Carries the HTTP status and headers of `source` (an axios-style error) over
 * to `target` (the mapped service error built from it), as a `response`
 * property shaped like axios's own.
 *
 * Why this exists: every interactor converts the axios error into an
 * `AppError` subclass, and `AppError` keeps only a code and a message. The
 * retry and recovery layers wrap the interactor, so they only ever see the
 * mapped error — and every one of them reads `error.response`:
 *
 * - `RetryMechanism` — `status`, `Retry-After`, `x-amzn-ErrorType`
 * - `createCriticalRetryOptions.isTerminal` — the 401/403 backstop
 * - `classifyIpAutoLoginError` — step-3 reason, provisioning fall-back
 * - `baseInteractor.executeGuarded` — the 401 token-refresh replay
 *
 * Without the response all of them saw "no HTTP status", which RetryMechanism
 * reads as a connectivity failure and retries. Re-attaching it here makes
 * those existing readers work unchanged instead of teaching each one a second
 * error shape.
 *
 * The property is non-enumerable so the headers stay out of anything that
 * serialises or spreads the error (logs, Redux payloads).
 *
 * A `source` with no numeric status — a transport failure, an abort, a
 * client-side validation error — leaves `target` untouched, so those keep
 * reading as "no response" exactly as before.
 *
 * @returns `target`, for chaining at the end of a builder.
 */
export function attachHttpContext<T extends object>(
  target: T,
  source: unknown
): T {
  const response: unknown = readProperty(source, "response");
  const rawStatus: unknown = readProperty(response, "status");
  const status: number =
    typeof rawStatus === "number" ? rawStatus : Number(rawStatus);
  if (rawStatus === undefined || rawStatus === null || Number.isNaN(status)) {
    return target;
  }
  const context: THttpContext = {
    status: status,
    headers: normaliseHeaders(readProperty(response, "headers"))
  };
  Object.defineProperty(target, "response", {
    value: context,
    enumerable: false,
    configurable: true,
    writable: true
  });
  const serviceCode: string | undefined = readBodyCode(
    readProperty(response, "data")
  );
  if (serviceCode !== undefined) {
    Object.defineProperty(target, SERVICE_ERROR_CODE_PROPERTY, {
      value: serviceCode,
      enumerable: false,
      configurable: true,
      writable: true
    });
  }
  return target;
}

/**
 * Where the backend's own error code is kept on a mapped error. Deliberately
 * **not** under `response.data`: `formatRetryErrorMessage` and the sign-in
 * alert read `response.data` and would then show the service's vocabulary
 * ("unauthorized") in place of the catalog code the user and support are meant
 * to see. This field is for classification only.
 */
const SERVICE_ERROR_CODE_PROPERTY: string = "serviceErrorCode";

/** The `code` from a `{ code }` or `{ error: { code } }` body, if any. */
function readBodyCode(data: unknown): string | undefined {
  const nested: unknown = readProperty(data, "error");
  const envelope: unknown =
    typeof nested === "object" && nested !== null ? nested : data;
  const code: unknown = readProperty(envelope, "code");
  return typeof code === "string" && code.length > 0 ? code : undefined;
}

/**
 * The backend's own error code (`AUTHS.DLID_PERMANENTLY_REMOVED`,
 * `IAS.PCID_NOT_FOUND`, …) recorded by {@link attachHttpContext}, or
 * `undefined`.
 *
 * For deciding *what to do* about a failure whose mapped catalog code is a
 * catch-all — a code the mapper has no branch for lands on its class's
 * `UNKNOWN` number, which says nothing about recovery. Never show it to users
 * or put it in place of the catalog code in telemetry.
 */
export function getServiceErrorCode(error: unknown): string | undefined {
  const code: unknown = readProperty(error, SERVICE_ERROR_CODE_PROPERTY);
  return typeof code === "string" && code.length > 0 ? code : undefined;
}

/**
 * The HTTP context previously attached by {@link attachHttpContext}, or null.
 */
export function getHttpContext(error: unknown): THttpContext | null {
  const response: unknown = readProperty(error, "response");
  const status: unknown = readProperty(response, "status");
  if (typeof status !== "number") {
    return null;
  }
  const headers: unknown = readProperty(response, "headers");
  return {
    status: status,
    headers:
      typeof headers === "object" && headers !== null
        ? (headers as Record<string, string>)
        : {}
  };
}
